import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/Share';
import DeleteIcon from '@mui/icons-material/Delete';
import PublishIcon from '@mui/icons-material/Publish';
import ArchiveIcon from '@mui/icons-material/Archive';
import { useTeacher } from '../../hooks/useTeacher';
import apiAxiosInstance from '../../utils/axiosInstance';
import AssignTestDialog from './AssignTestDialog';
import VideoHelpButton from '../Common/VideoHelpButton';

interface Test {
  _id: string;
  title: string;
  description: string;
  gradeLevel: string;
  status: string;
  isPublic: boolean;
  createdAt: string;
  totalQuestions: number;
}

const GradeTests: React.FC = () => {
  const navigate = useNavigate();
  const { teacher } = useTeacher();
  const [tests, setTests] = useState<Test[]>([]);
  const [filteredTests, setFilteredTests] = useState<Test[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updateLoading, setUpdateLoading] = useState<string | null>(null);
  
  // Filter states
  const [nameFilter, setNameFilter] = useState('');
  const [gradeLevelFilter, setGradeLevelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Delete confirmation states
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [testToDelete, setTestToDelete] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  // Assign dialog state
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTestId, setAssignTestId] = useState<string | null>(null);
  const [assignTestTitle, setAssignTestTitle] = useState<string | undefined>(undefined);
  
  // Assignment data
  const [testAssignments, setTestAssignments] = useState<{[testId: string]: any[]}>({});

  useEffect(() => {
    const fetchTests = async () => {
      try {
        const response = await apiAxiosInstance.get('/api/tests');
        // Sort tests by date (newest first) before setting state
        const sortedTests = response.data.sort((a: Test, b: Test) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setTests(sortedTests);
        setFilteredTests(sortedTests);
        
        // Fetch assignments for each test
        const assignmentPromises = sortedTests.map((test: Test) =>
          apiAxiosInstance.get('/api/assignments', { params: { testId: test._id } })
            .then(res => ({ testId: test._id, assignments: res.data || [] }))
            .catch(() => ({ testId: test._id, assignments: [] }))
        );
        
        const assignmentResults = await Promise.all(assignmentPromises);
        const assignmentMap: {[testId: string]: any[]} = {};
        assignmentResults.forEach(result => {
          assignmentMap[result.testId] = result.assignments;
        });
        setTestAssignments(assignmentMap);
      } catch (err) {
        setError('Error loading tests. Please try again.');
        console.error('Error fetching tests:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTests();
  }, []);

  // Apply filters whenever filter values change
  useEffect(() => {
    let filtered = [...tests];

    // Apply name filter
    if (nameFilter) {
      filtered = filtered.filter(test => 
        test.title.toLowerCase().includes(nameFilter.toLowerCase())
      );
    }

    // Apply grade level filter
    if (gradeLevelFilter !== 'all') {
      filtered = filtered.filter(test => 
        test.gradeLevel === gradeLevelFilter
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(test => 
        test.status === statusFilter
      );
    }

    setFilteredTests(filtered);
  }, [tests, nameFilter, gradeLevelFilter, statusFilter]);

  const handleStatusChange = async (testId: string, newStatus: 'published' | 'archived' | 'draft') => {
    try {
      setUpdateLoading(testId);
      await apiAxiosInstance.patch(`/api/tests/${testId}/status`, { status: newStatus });
      
      // Update local state
      setTests(prevTests => 
        prevTests.map(test => 
          test._id === testId 
            ? { ...test, status: newStatus }
            : test
        )
      );
    } catch (error) {
      console.error('Error updating test status:', error);
      setError('Failed to update test status. Please try again.');
    } finally {
      setUpdateLoading(null);
    }
  };

  const handleDeleteClick = (testId: string) => {
    setTestToDelete(testId);
    setDeleteConfirmOpen(true);
  };

  const handleOpenAssign = (test: Test) => {
    setAssignTestId(test._id);
    setAssignTestTitle(test.title);
    setAssignOpen(true);
  };

  const handleAssigned = () => {
    setSnackbar({ open: true, message: 'Assignment links created!', severity: 'success' });
  };

  const handleDeleteConfirm = async () => {
    if (!testToDelete) return;

    try {
      await apiAxiosInstance.delete(`/api/tests/${testToDelete}`);
      setTests(prevTests => prevTests.filter(test => test._id !== testToDelete));
      setDeleteConfirmOpen(false);
      setTestToDelete(null);
      setSnackbar({
        open: true,
        message: 'Test deleted successfully',
        severity: 'success'
      });
    } catch (error: any) {
      console.error('Delete test error:', error);
      
      let errorMessage = 'Error deleting test';
      
      if (error.response?.data?.message) {
        const backendMessage = error.response.data.message;
        if (backendMessage.includes('existing submissions')) {
          errorMessage = 'Cannot delete test - students have already submitted responses. You can archive the test instead.';
        } else {
          errorMessage = backendMessage;
        }
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      
      setDeleteConfirmOpen(false);
      setTestToDelete(null);
    }
  };

  const getStatusButton = (test: Test) => {
    if (updateLoading === test._id) {
      return (
        <IconButton disabled>
          <CircularProgress size={24} />
        </IconButton>
      );
    }

    switch (test.status) {
      case 'draft':
      case 'published':
        return (
          <Tooltip title="Archive Test">
            <IconButton 
              color="warning"
              onClick={() => handleStatusChange(test._id, 'archived')}
            >
              <ArchiveIcon />
            </IconButton>
          </Tooltip>
        );
      case 'archived':
        return (
          <Tooltip title="Restore to Published">
            <IconButton 
              onClick={() => handleStatusChange(test._id, 'published')}
            >
              <PublishIcon />
            </IconButton>
          </Tooltip>
        );
      default:
        return null;
    }
  };

  const renderAssignmentInfo = (test: Test) => {
    const assignments = testAssignments[test._id] || [];
    
    if (assignments.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          Not assigned
        </Typography>
      );
    }

    const allStudentsAssignment = assignments.find(a => a.assignmentType === 'allStudents');
    if (allStudentsAssignment) {
      return (
        <Tooltip title="Includes all currently enrolled students in this class">
          <Chip
            label="All Students"
            size="small"
            color="primary"
            variant="outlined"
          />
        </Tooltip>
      );
    }

    const groupAssignments = assignments.filter(a => a.assignmentType === 'group');
    if (groupAssignments.length > 0) {
      return (
        <Typography variant="body2">
          {groupAssignments.length} group{groupAssignments.length > 1 ? 's' : ''}
        </Typography>
      );
    }

    return (
      <Typography variant="body2" color="text.secondary">
        Not assigned
      </Typography>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4">
            My Tests
          </Typography>
          <VideoHelpButton 
            url="https://www.youtube.com/watch?v=sXJV8CQ7b7c"
            title="How to Manage Your Tests"
            buttonLabel="Watch tutorial on managing tests"
          />
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/tests/create')}
        >
          Create New Test
        </Button>
      </Box>

      {/* Filter Controls */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Search by name"
            variant="outlined"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Grade Level</InputLabel>
            <Select
              value={gradeLevelFilter}
              label="Grade Level"
              onChange={(e) => setGradeLevelFilter(e.target.value)}
            >
              <MenuItem value="all">All Grades</MenuItem>
              <MenuItem value="PK">PreK</MenuItem>
              <MenuItem value="K">Kindergarten</MenuItem>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((grade) => (
                <MenuItem key={grade} value={grade.toString()}>
                  Grade {grade}
                </MenuItem>
              ))}
              <MenuItem value="AE">Adult Education</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Grade Level</TableCell>
              <TableCell>Questions</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTests.map((test) => (
              <TableRow key={test._id}>
                <TableCell>{test.title}</TableCell>
                <TableCell>Grade {test.gradeLevel}</TableCell>
                <TableCell>{test.totalQuestions} questions</TableCell>
                  <TableCell>
                  <Chip
                    label={test.status}
                    color={test.status === 'draft' ? 'warning' : 'success'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {renderAssignmentInfo(test)}
                </TableCell>
                <TableCell>
                  {new Date(test.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    {getStatusButton(test)}
                    <IconButton 
                      onClick={() => navigate(`/tests/edit/${test._id}`)}
                      disabled={test.status === 'published'}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <Tooltip title={test.status === 'published' ? 'Published tests cannot be deleted' : 'Delete test'}>
                      <span>
                        <IconButton 
                          color="error"
                          disabled={test.status === 'published'}
                          onClick={() => handleDeleteClick(test._id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => navigate(`/test/${test._id}`)}
                      sx={{ mr: 1 }}
                    >
                      Review Test
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      startIcon={<ShareIcon />}
                      onClick={() => handleOpenAssign(test)}
                    >
                      Group Assignments
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => navigate(`/tests/grade/${test._id}`)}
                    >
                      View Submissions
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {filteredTests.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body1" color="textSecondary">
                    {tests.length === 0 ? 
                      'No tests found. Create your first test to get started.' :
                      'No tests match your filters.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete Test</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Are you sure you want to delete this test? This action cannot be undone.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> If students have already submitted responses to this test, 
              deletion will be prevented. In that case, consider archiving the test instead.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete Test
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Assign Dialog */}
      <AssignTestDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        testId={assignTestId || ''}
        testTitle={assignTestTitle}
        onAssigned={handleAssigned}
      />
    </Box>
  );
};

export default GradeTests; 